import {
  EVENT_KEY_ARROW_LEFT,
  EVENT_KEY_ARROW_RIGHT,
  EVENT_KEY_ENTER,
  EVENT_KEY_ESCAPE,
  EVENT_KEY_SPACE_BAR,
  EVENT_NAME_KEY_DOWN,
} from '../constants/event'
import { BACKSPACE_KEY, DELETE_KEY } from '../constants/key'
import { MAIN_MOUSE_BUTTON, MIDDLE_MOUSE_BUTTON } from '../constants/mouse'

export function preventDefault(event) {
  event?.preventDefault()
}

export function stopPropagation(event) {
  event?.stopPropagation()
}

/**
 * Whether the event fired on the element the handler is attached to, rather
 * than bubbling up from a descendant. Handy for backdrop clicks and for
 * reacting only to an element's own animation/transition events.
 */
export function isEventTargetSelf(event) {
  return event.target === event.currentTarget
}

/**
 * Whether the event comes from the element's own DOM. React bubbles events from
 * a portal (a dropdown, a tooltip) through its React ancestors, whose DOM does
 * not contain the portal.
 */
export function isEventFromOwnDom(event) {
  return event.currentTarget.contains(event.target)
}

export function isRepeatedClick(event) {
  return event.detail > 1
}

/**
 * Whether a keydown is a plain Enter meant to submit: Enter, but not Shift+Enter
 * (a newline) and not the Enter that confirms an IME composition (e.g. CJK).
 * Works with both React synthetic and native keyboard events.
 */
export function isSubmitKeyDown(event) {
  return (
    event.key === EVENT_KEY_ENTER &&
    !event.shiftKey &&
    !(event.nativeEvent ?? event).isComposing
  )
}

export function isEventKeyEnter(event) {
  return event.key === EVENT_KEY_ENTER
}

export function isEventKeySpaceBar(event) {
  return event.key === EVENT_KEY_SPACE_BAR
}

export function isEventKeyEscape(event) {
  return event.key === EVENT_KEY_ESCAPE
}

export function isEventKeyArrowLeft(event) {
  return event.key === EVENT_KEY_ARROW_LEFT
}

export function isEventKeyArrowRight(event) {
  return event.key === EVENT_KEY_ARROW_RIGHT
}

export function isEventKeyBackspace(event) {
  return event.key === BACKSPACE_KEY
}

export function isEventKeyDelete(event) {
  return event.key === DELETE_KEY
}

export function isKeyDownEvent(event) {
  return event.type === EVENT_NAME_KEY_DOWN
}

export function isMainMouseButton(event) {
  return event.button === MAIN_MOUSE_BUTTON
}

export function isMiddleMouseButton(event) {
  return event.button === MIDDLE_MOUSE_BUTTON
}

/**
 * The {@link addEventListener} function adds a new handler for the
 * given `event`.
 *
 * @param {EventTarget} target - Target of event
 * @param {string} event - Event to listen
 * @param {EventListener | EventListenerObject} listener - Event listener
 * @param {AddEventListenerOptions | boolean} [flags] - Options
 *
 * @returns {() => void} - A function to remove added event listener
 */
export function addEventListener(target, event, listener, flags = false) {
  target.addEventListener(event, listener, flags)

  return () => {
    target.removeEventListener(event, listener, flags)
  }
}

export class EventTargets {
  /**
   * @type {string}
   */
  #type

  /**
   * @type {EventListener | EventListenerObject}
   */
  #listener

  /**
   * @type {AddEventListenerOptions | boolean}
   */
  #options

  /**
   * @type {Set<EventTarget>}
   */
  #targets

  /**
   * @param type
   * @param {EventListener | EventListenerObject} listener
   * @param {AddEventListenerOptions | boolean} options
   */
  constructor(type, listener, options = false) {
    this.#type = type
    this.#listener = listener
    this.#options = options
    this.#targets = new Set()
  }

  /**
   * @param {EventTarget} target
   *
   * @returns {EventTargets}
   */
  add(target) {
    if (!this.#targets.has(target)) {
      this.#targets.add(target)
      target.addEventListener(this.#type, this.#listener, this.#options)
    }

    return this
  }

  /**
   * @param {EventTarget} target
   *
   * @returns {EventTargets}
   */
  remove(target) {
    if (this.#targets.has(target)) {
      this.#targets.delete(target)
      target.removeEventListener(this.#type, this.#listener, this.#options)
    }

    return this
  }

  /**
   * @returns {EventTargets}
   */
  removeAll() {
    this.#targets.forEach(target => this.remove(target))
  }
}
